import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/ui/Icon';
import Mascot from '../../components/ui/Mascot';
import {api, setToken} from '../../lib/api';
import styles from './Login.module.scss';

export default function Login () {
    const navigate = useNavigate();
    const [accountType, setAccountType] = useState('student');
    const [mode, setMode] = useState('login');
    const [name, setName] = useState('');
    const [secret, setSecret] = useState('');
    const [adminToken, setAdminToken] = useState(localStorage.getItem('cq_admin_token') || '');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const isRegister = mode === 'register';
    const isAdmin = accountType === 'admin';

    const handleSubmit = async e => {
        e.preventDefault();
        setError('');
        if (isAdmin) {
            if (!adminToken.trim()) {
                setError('请输入管理员令牌');
                return;
            }
            setLoading(true);
            try {
                localStorage.setItem('cq_admin_token', adminToken.trim());
                await api.verifyAdmin();
                navigate('/admin/courses');
            } catch (err) {
                localStorage.removeItem('cq_admin_token');
                setError(err.message || '管理员登录失败');
            } finally {
                setLoading(false);
            }
            return;
        }
        const username = name.trim();
        if (!username || !secret) {
            setError('请填写昵称和口令');
            return;
        }
        if (secret.length < 4) {
            setError('口令至少 4 位');
            return;
        }
        setLoading(true);
        try {
            const payload = {username, secret};
            const res = isRegister
                ? await api.register({...payload, displayName: username})
                : await api.login(payload);
            setToken(res.token);
            navigate('/profile');
        } catch (err) {
            setError(err.message || '出现了一点小问题，请稍后再试');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`cq-container ${styles.page}`}>
            <div className={styles.card}>
                <div className={styles.welcome}>
                    <span className={styles.dot1} />
                    <span className={styles.dot2} />
                    <h1 className={styles.welcomeTitle}>欢迎来到<br />CodeQuest！</h1>
                    <div className={styles.mascotRing}>
                        <Mascot size={150} />
                    </div>
                </div>

                <form className={styles.form} onSubmit={handleSubmit} noValidate aria-busy={loading}>
                    <div className={styles.accountSwitch} role="tablist" aria-label="登录类型">
                        <button
                            type="button"
                            className={[styles.switchBtn, !isAdmin ? styles.switchActive : ''].join(' ')}
                            onClick={() => {
                                setAccountType('student');
                                setError('');
                            }}
                        >
                            学生登录
                        </button>
                        <button
                            type="button"
                            className={[styles.switchBtn, isAdmin ? styles.switchActive : ''].join(' ')}
                            onClick={() => {
                                setAccountType('admin');
                                setMode('login');
                                setError('');
                            }}
                        >
                            管理员登录
                        </button>
                    </div>

                    <h2 className={styles.formTitle}>{isAdmin ? '进入课程后台' : isRegister ? '创建你的账号' : '准备好出发了吗？'}</h2>
                    <p className={styles.formSub}>
                        {isAdmin ? '使用管理员令牌维护课程、步骤和验收标准。' : isRegister ? '取个好听的昵称，开启编程冒险。' : '输入你的信息，继续你的冒险旅程。'}
                    </p>

                    {isAdmin ? (
                        <>
                            <label className={styles.label} htmlFor="admin-token">管理员令牌</label>
                            <div className={styles.field}>
                                <Icon name="lock" size={20} className={styles.fieldIcon} />
                                <input
                                    id="admin-token"
                                    name="admin-token"
                                    type="password"
                                    className={styles.input}
                                    placeholder="输入 X-Admin-Token"
                                    value={adminToken}
                                    onChange={e => setAdminToken(e.target.value)}
                                    autoComplete="off"
                                    required
                                    aria-invalid={Boolean(error)}
                                    aria-describedby={error ? 'login-error' : 'admin-hint'}
                                />
                            </div>
                            <p id="admin-hint" className={styles.adminHint}>本地开发默认令牌为 dev-admin-token。</p>
                        </>
                    ) : (
                        <>
                            <label className={styles.label} htmlFor="explorer-name">探险家昵称</label>
                            <div className={styles.field}>
                                <Icon name="users" size={20} className={styles.fieldIcon} />
                                <input
                                    id="explorer-name"
                                    name="username"
                                    className={styles.input}
                                    placeholder="你的昵称"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    autoComplete="username"
                                    required
                                    aria-invalid={Boolean(error)}
                                    aria-describedby={error ? 'login-error' : undefined}
                                />
                            </div>

                            <label className={styles.label} htmlFor="secret-code">秘密口令</label>
                            <div className={styles.field}>
                                <Icon name="lock" size={20} className={styles.fieldIcon} />
                                <input
                                    id="secret-code"
                                    name="password"
                                    type="password"
                                    className={styles.input}
                                    placeholder="你的秘密密码"
                                    value={secret}
                                    onChange={e => setSecret(e.target.value)}
                                    autoComplete={isRegister ? 'new-password' : 'current-password'}
                                    required
                                    minLength={4}
                                    aria-invalid={Boolean(error)}
                                    aria-describedby={error ? 'login-error' : undefined}
                                />
                            </div>
                        </>
                    )}

                    {error && <div id="login-error" className={styles.error} role="alert">{error}</div>}

                    <Button type="submit" variant="success" size="lg" block disabled={loading}>
                        {loading ? '稍等一下…' : isAdmin ? '进入后台' : isRegister ? '创建并出发！' : '出发啦！'}
                    </Button>

                    {!isAdmin && (
                        <>
                            <div className={styles.divider}><span>或者</span></div>

                            <Button
                                type="button"
                                variant="soft"
                                size="md"
                                block
                                iconLeft={<Icon name="graduation" size={20} />}
                                className={styles.classroom}
                            >
                                使用班级邀请码
                            </Button>

                            <div className={styles.joinRow}>
                                <span>{isRegister ? '已经有账号？' : '还没有账号？'}</span>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    iconRight={<Icon name="map" size={18} />}
                                    onClick={() => {
                                        setError('');
                                        setMode(isRegister ? 'login' : 'register');
                                    }}
                                >
                                    {isRegister ? '去登录' : '加入冒险'}
                                </Button>
                            </div>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
}
